'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import {
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  Globe,
  MessageCircle,
  MessageSquare,
  Phone,
} from 'lucide-react';
import TransitionLink from './TransitionLink';
import { NavIcon } from './NavIcon';
import { NavBadge } from './NavBadge';
import type {
  IntegrationCategory,
  NavLabels,
  NavSection,
  NavUtility,
} from '@/lib/get-navbar-data';

interface MobileMenuProps {
  isOpen: boolean;
  sections: NavSection[];
  utility: NavUtility;
  labels: NavLabels;
  lang: string;
  topOffset: number;
  onLinkClick: () => void;
  onOpenChat: () => void;
}

const LANG_LABELS: Record<string, string> = {
  'en-us': 'EN',
  'zh-cn': '简',
  'zh-hk': '繁',
};

type DropdownSection = Extract<NavSection, { type: 'standard' | 'icon_row' | 'integrations' }>;

const isDropdown = (s: NavSection): s is DropdownSection =>
  s.type === 'standard' || s.type === 'icon_row' || s.type === 'integrations';

export function MobileMenu({
  isOpen,
  sections,
  utility,
  labels,
  lang,
  topOffset,
  onLinkClick,
  onOpenChat,
}: MobileMenuProps) {
  const [activeSub, setActiveSub] = useState<string | null>(null);
  // Treat the menu-closed state as having no active subpane, so subpanes
  // slide back out even before the wipe-close finishes.
  const effectiveActiveSub = isOpen ? activeSub : null;

  const logoSection = sections.find((s) => s.type === 'logo');
  const ctaSections = sections.filter((s): s is Extract<NavSection, { type: 'cta' }> => s.type === 'cta');
  const navSections = sections.filter((s) => s.type !== 'logo' && s.type !== 'cta');
  const dropdownSections = navSections.filter(isDropdown);

  const closeSub = () => setActiveSub(null);
  const openSub = (header: string) => setActiveSub(header);

  const handleNavigate = () => {
    setActiveSub(null);
    onLinkClick();
  };

  const ghostCta = ctaSections.find((c) => c.style === 'ghost') ?? ctaSections[0];
  const primaryCta = ctaSections.find((c) => c.style !== 'ghost') ?? ctaSections[1];
  const menuShellStyle: React.CSSProperties = {
    top: topOffset,
    bottom: 0,
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="mobile-menu"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-x-0 z-40 overflow-hidden lg:hidden"
          style={menuShellStyle}
        >
          {/* Wipe-in inner — the fixed shell already starts below the visible navbar. */}
          <motion.div
            className="absolute inset-0 flex flex-col bg-white"
            initial={{ clipPath: 'inset(0 100% 0 0)' }}
            animate={{ clipPath: 'inset(0 0 0 0)' }}
            exit={{ clipPath: 'inset(0 100% 0 0)' }}
            transition={{ duration: 0.46, ease: [0.2, 0.8, 0.2, 1] }}
          >
            {/* ROOT PANE */}
            <div
              className="relative h-full overflow-y-auto bg-white pb-[calc(120px+env(safe-area-inset-bottom))] transition-transform duration-[360ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]"
              style={{ transform: effectiveActiveSub ? 'translateX(-22%)' : 'translateX(0)' }}
            >
              {/* Utility row — hidden for now */}
              <div className="hidden grid-cols-3 gap-2 p-3.5">
                {utility.phoneLabel && (
                  <a
                    href={utility.phoneUrl || '#'}
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-[#D1EAE7] bg-[#E8F4F2] px-2 py-2.5 text-[12px] font-semibold text-[#0F5E58] no-underline transition-colors hover:bg-[#D1EAE7]"
                  >
                    <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">Call</span>
                  </a>
                )}
                {utility.whatsappLabel && (
                  <a
                    href={utility.whatsappUrl || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-1.5 rounded-xl border border-[#D1EAE7] bg-[#E8F4F2] px-2 py-2.5 text-[12px] font-semibold text-[#0F5E58] no-underline transition-colors hover:bg-[#D1EAE7]"
                  >
                    <MessageCircle className="h-3.5 w-3.5 flex-shrink-0" />
                    <span className="truncate">WhatsApp</span>
                  </a>
                )}
                <a
                  href={`/${lang}/`}
                  className="flex items-center justify-center gap-1.5 rounded-xl border border-[#E4E7EA] bg-white px-2 py-2.5 text-[12px] font-semibold text-[#1B2A33] no-underline transition-colors hover:border-[#1FA39A]"
                >
                  <Globe className="h-3.5 w-3.5 flex-shrink-0 text-[#157A73]" />
                  <span>{LANG_LABELS[lang] ?? lang.toUpperCase()}</span>
                </a>
              </div>

              {/* Home shortcut */}
              {logoSection && (
                <TransitionLink
                  href={`/${lang}${logoSection.url}`}
                  onClick={handleNavigate}
                  className="flex w-full items-center justify-between border-b border-[#E4E7EA] bg-transparent px-5 py-[18px] text-left text-[16px] font-semibold tracking-[-0.01em] text-[#1B2A33] no-underline transition-[background,padding] duration-200 hover:bg-[#FAFBFB] hover:pl-6"
                >
                  <span>Home</span>
                </TransitionLink>
              )}

              {/* Root items */}
              {navSections.map((section) => {
                if (section.type === 'link') {
                  return (
                    <TransitionLink
                      key={section.header}
                      href={`/${lang}${section.url}`}
                      onClick={handleNavigate}
                      className="flex w-full items-center justify-between border-b border-[#E4E7EA] bg-transparent px-5 py-[18px] text-left text-[16px] font-semibold tracking-[-0.01em] text-[#1B2A33] no-underline transition-[background,padding] duration-200 hover:bg-[#FAFBFB] hover:pl-6"
                    >
                      <span>{section.header}</span>
                    </TransitionLink>
                  );
                }
                if (isDropdown(section)) {
                  return (
                    <button
                      key={section.header}
                      type="button"
                      onClick={() => openSub(section.header)}
                      className="flex w-full items-center justify-between border-b border-[#E4E7EA] bg-transparent px-5 py-[18px] text-left text-[16px] font-semibold tracking-[-0.01em] text-[#1B2A33] transition-[background,padding] duration-200 hover:bg-[#FAFBFB] hover:pl-6"
                      aria-expanded={effectiveActiveSub === section.header}
                    >
                      <span>
                        {section.header === 'Services' && utility.mobileServicesPrefix
                          ? `${utility.mobileServicesPrefix} ${section.header}`
                          : section.header}
                      </span>
                      <ChevronRight className="h-4 w-4 text-[#8A99A3]" strokeWidth={2.4} />
                    </button>
                  );
                }
                return null;
              })}
            </div>

            {/* SUBPANES — one per dropdown section, each absolutely positioned */}
            {dropdownSections.map((section) => (
              <SubPane
                key={section.header}
                section={section}
                active={effectiveActiveSub === section.header}
                lang={lang}
                onBack={closeSub}
                onNavigate={handleNavigate}
                mobileServicesPrefix={utility.mobileServicesPrefix}
                labels={labels}
              />
            ))}

            {/* Footer */}
            <div className="absolute inset-x-0 bottom-0 z-20 flex gap-2.5 border-t border-[#E4E7EA] bg-white px-4 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-3.5 shadow-[0_-8px_16px_-6px_rgba(14,19,24,0.08),0_-20px_28px_-16px_rgba(14,19,24,0.10)]">
              {ghostCta && (
                <TransitionLink
                  href={`/${lang}${ghostCta.url}`}
                  onClick={handleNavigate}
                  className="flex h-12 flex-1 items-center justify-center gap-2 rounded-[10px] border-[1.5px] border-[#E4E7EA] bg-white text-[14px] font-bold tracking-[-0.005em] text-[#1B2A33] no-underline transition-colors hover:border-[#1FA39A] hover:text-[#157A73]"
                >
                  {ghostCta.header}
                  <ArrowRight className="h-4 w-4" />
                </TransitionLink>
              )}
              {primaryCta && primaryCta.action === 'open_chat' && (
                <button
                  type="button"
                  onClick={() => {
                    onOpenChat();
                    onLinkClick();
                  }}
                  className="flex h-12 flex-1 items-center justify-center gap-2 rounded-[10px] border-0 bg-[#1FA39A] text-[14px] font-bold tracking-[-0.005em] text-white shadow-[0_6px_14px_-4px_rgba(31,163,154,0.5)] transition-colors hover:bg-[#157A73]"
                >
                  <MessageSquare className="h-4 w-4" />
                  {primaryCta.header}
                </button>
              )}
              {primaryCta && primaryCta.action !== 'open_chat' && (
                <TransitionLink
                  href={`/${lang}${primaryCta.url}`}
                  onClick={handleNavigate}
                  className="flex h-12 flex-1 items-center justify-center gap-2 rounded-[10px] border-0 bg-[#1FA39A] text-[14px] font-bold tracking-[-0.005em] text-white no-underline shadow-[0_6px_14px_-4px_rgba(31,163,154,0.5)] transition-colors hover:bg-[#157A73]"
                >
                  <MessageSquare className="h-4 w-4" />
                  {primaryCta.header}
                </TransitionLink>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ────────────────────────────────────────────────────────────────────────────

interface SubPaneProps {
  section: DropdownSection;
  active: boolean;
  lang: string;
  onBack: () => void;
  onNavigate: () => void;
  mobileServicesPrefix: string;
  labels: NavLabels;
}

function SubPane({ section, active, lang, onBack, onNavigate, mobileServicesPrefix, labels }: SubPaneProps) {
  return (
    <div
      className="absolute inset-0 z-10 overflow-y-auto bg-white pb-[calc(120px+env(safe-area-inset-bottom))] transition-transform duration-[380ms] ease-[cubic-bezier(0.2,0.8,0.2,1)]"
      style={{
        transform: active ? 'translateX(0)' : 'translateX(100%)',
        boxShadow: active ? '-10px 0 30px -10px rgba(14, 19, 24, 0.12)' : undefined,
      }}
      aria-hidden={!active}
    >
      <div className="sticky top-0 z-[3] flex h-14 items-center gap-1 border-b border-[#E4E7EA] bg-white px-2">
        <button
          type="button"
          onClick={onBack}
          className="grid h-10 w-10 place-items-center rounded-[11px] border-0 bg-transparent text-[#3E5460] transition-colors hover:bg-[#E8F4F2] hover:text-[#157A73]"
          aria-label="Back"
        >
          <ChevronLeft className="h-[18px] w-[18px]" strokeWidth={2.4} />
        </button>
        <span className="text-[16px] font-bold tracking-[-0.01em] text-[#1B2A33]">
          {section.header === 'Services' && mobileServicesPrefix
            ? `${mobileServicesPrefix} ${section.header}`
            : section.header}
        </span>
      </div>

      <div className="pt-1">
        {section.type === 'standard' && (
          <StandardSubBody section={section} lang={lang} onNavigate={onNavigate} labels={labels} />
        )}
        {section.type === 'icon_row' && (
          <IconRowSubBody section={section} lang={lang} onNavigate={onNavigate} />
        )}
        {section.type === 'integrations' && (
          <IntegrationsSubBody section={section} lang={lang} onNavigate={onNavigate} active={active} />
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────

interface StandardSubBodyProps {
  section: Extract<NavSection, { type: 'standard' }>;
  lang: string;
  onNavigate: () => void;
  labels: NavLabels;
}

function StandardSubBody({ section, lang, onNavigate, labels }: StandardSubBodyProps) {
  const flagship = section.items.find((i) => i.isFeatured) ?? null;
  const rows = section.items.filter((i) => i !== flagship);

  return (
    <>
      {flagship && (
        <TransitionLink
          href={`/${lang}${flagship.url}`}
          onClick={onNavigate}
          className="mx-3.5 mb-2 mt-3.5 block rounded-2xl border border-[#D1EAE7] p-4 pb-[18px] text-inherit no-underline transition-transform duration-200 hover:-translate-y-px"
          style={{ background: 'linear-gradient(135deg, #E8F4F2 0%, #F4F2EE 100%)' }}
        >
          {section.eyebrow && (
            <p className="m-0 mb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#157A73]">
              {section.eyebrow}
            </p>
          )}
          <h3 className="m-0 mb-2 flex items-center gap-2 text-[20px] font-semibold leading-[1.15] tracking-[-0.015em] text-[#1B2A33]">
            {flagship.name}
            {flagship.badge && <NavBadge label={flagship.badge} />}
          </h3>
          {flagship.description && (
            <p className="m-0 mb-3.5 text-[12.5px] leading-[1.45] text-[#5B6B75]">
              {flagship.description}
            </p>
          )}
          <span className="inline-flex items-center gap-1.5 text-[12px] font-bold tracking-[0.02em] text-[#157A73]">
            {labels.explore}
            <ChevronRight className="h-3 w-3" strokeWidth={2.4} />
          </span>
        </TransitionLink>
      )}

      {section.promo && (
        <TransitionLink
          href={`/${lang}${section.promo.url}`}
          onClick={onNavigate}
          className="mx-3.5 mb-2 flex items-center gap-3.5 rounded-2xl p-4 text-white no-underline transition-transform duration-200 hover:-translate-y-px"
          style={{
            background:
              'radial-gradient(circle at 85% -20%, rgba(255,255,255,0.18) 0%, transparent 55%), linear-gradient(155deg, #0F5E58 0%, #1FA39A 100%)',
          }}
        >
          <span className="grid h-[42px] w-[42px] flex-shrink-0 place-items-center rounded-[11px] bg-white/20 text-white">
            <NavIcon
              keyword={section.promo.iconKey}
              fallback={MessageSquare}
              className="h-5 w-5"
            />
          </span>
          <div className="flex-1">
            <p className="m-0 mb-0.5 text-[15px] font-semibold leading-[1.2] tracking-[-0.01em] text-white">
              {section.promo.title}
            </p>
            {section.promo.subtitle && (
              <p className="m-0 text-[12px] leading-[1.35] text-white/85">
                {section.promo.subtitle}
              </p>
            )}
          </div>
          <ChevronRight className="ml-auto h-4 w-4 opacity-85" strokeWidth={2.4} />
        </TransitionLink>
      )}

      {rows.length > 0 && (
        <div className="mx-[26px] my-[18px] mb-1.5 mt-5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8A99A3]">
          {labels.other} {section.header.toLowerCase()}
        </div>
      )}

      {rows.map((item) => (
        <TransitionLink
          key={item.url}
          href={`/${lang}${item.url}`}
          onClick={onNavigate}
          className="grid grid-cols-[40px_1fr_16px] items-center gap-3.5 px-5 py-3 text-inherit no-underline transition-[background,padding] duration-200 hover:bg-[#FAFBFB] hover:pl-6"
        >
          <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-[10px] bg-[#E8F4F2] text-[#157A73]">
            <NavIcon keyword={item.iconKey} className="h-[18px] w-[18px]" strokeWidth={1.8} />
          </span>
          <div className="min-w-0">
            <p className="m-0 mb-0.5 flex items-center gap-2 text-[14.5px] font-semibold leading-[1.3] tracking-[-0.005em] text-[#1B2A33]">
              {item.name}
              {item.badge && <NavBadge label={item.badge} />}
            </p>
            {item.description && (
              <p className="m-0 text-[11.5px] leading-[1.4] text-[#5B6B75]">
                {item.description}
              </p>
            )}
          </div>
          <ChevronRight className="h-3.5 w-3.5 text-[#8A99A3]" strokeWidth={2.4} />
        </TransitionLink>
      ))}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────

interface IconRowSubBodyProps {
  section: Extract<NavSection, { type: 'icon_row' }>;
  lang: string;
  onNavigate: () => void;
}

function IconRowSubBody({ section, lang, onNavigate }: IconRowSubBodyProps) {
  return (
    <>
      {section.items.map((item) => (
        <TransitionLink
          key={item.url}
          href={`/${lang}${item.url}`}
          onClick={onNavigate}
          className="grid grid-cols-[40px_1fr_16px] items-center gap-3.5 px-5 py-3 text-inherit no-underline transition-[background,padding] duration-200 hover:bg-[#FAFBFB] hover:pl-6"
        >
          <span className="grid h-10 w-10 flex-shrink-0 place-items-center overflow-hidden rounded-[10px] bg-[#E8F4F2] text-[#157A73]">
            {item.icon && (
              <Image
                src={item.icon}
                alt=""
                width={32}
                height={32}
                className="h-7 w-7 object-contain"
              />
            )}
          </span>
          <p className="m-0 text-[14.5px] font-semibold leading-[1.3] tracking-[-0.005em] text-[#1B2A33]">
            {item.name}
          </p>
          <ChevronRight className="h-3.5 w-3.5 text-[#8A99A3]" strokeWidth={2.4} />
        </TransitionLink>
      ))}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────

interface IntegrationsSubBodyProps {
  section: Extract<NavSection, { type: 'integrations' }>;
  lang: string;
  onNavigate: () => void;
  active: boolean;
}

function IntegrationsSubBody({ section, lang, onNavigate, active }: IntegrationsSubBodyProps) {
  const categories = useMemo(
    () => section.categories.filter((c) => c.integrations.length > 0),
    [section.categories],
  );
  const [activeTag, setActiveTag] = useState<string>(categories[0]?.tag ?? '');
  const activeCategory: IntegrationCategory | undefined =
    categories.find((c) => c.tag === activeTag) ?? categories[0];

  const viewAllText = section.viewAllButton.replace('#', String(section.totalCount));

  // One-time "peek" entry animation: snap scroll to 100px then ease back to 0
  // with a slow ease-out, signalling that the chip row scrolls horizontally.
  const chipsRef = useRef<HTMLDivElement>(null);
  const hasPeeked = useRef(false);
  useEffect(() => {
    if (!active || hasPeeked.current) return;
    const el = chipsRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    hasPeeked.current = true;
    const PEEK = 100;
    const DELAY_MS = 420; // wait for subpane slide-in to settle
    const DURATION_MS = 850; // "very gentle"
    el.scrollLeft = PEEK;
    let rafId = 0;
    const delayTimer = window.setTimeout(() => {
      const start = performance.now();
      const tick = (now: number) => {
        const t = Math.min(1, (now - start) / DURATION_MS);
        const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
        el.scrollLeft = PEEK * (1 - eased);
        if (t < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }, DELAY_MS);
    return () => {
      window.clearTimeout(delayTimer);
      cancelAnimationFrame(rafId);
    };
  }, [active]);

  return (
    <>
      {/* Chips — touch-action: pan-x locks vertical play so chip taps don't drift the row up/down */}
      <div
        ref={chipsRef}
        className="flex gap-1.5 overflow-x-auto overflow-y-hidden px-3.5 pb-1 pt-3 [-ms-overflow-style:none] [overscroll-behavior-y:contain] [scrollbar-width:none] [touch-action:pan-x] [&::-webkit-scrollbar]:hidden"
      >
        {categories.map((cat) => {
          const isActive = cat.tag === activeCategory?.tag;
          return (
            <button
              key={cat.tag}
              type="button"
              onClick={() => setActiveTag(cat.tag)}
              className={`flex-shrink-0 whitespace-nowrap rounded-full border px-3.5 py-2 text-[12.5px] font-semibold transition-colors ${
                isActive
                  ? 'border-[#1B2A33] bg-[#1B2A33] text-white'
                  : 'border-[#E4E7EA] bg-[#FAFBFB] text-[#3E5460] hover:border-[#D1EAE7] hover:text-[#157A73]'
              }`}
            >
              {cat.label}
            </button>
          );
        })}
      </div>

      <div className="mx-[26px] my-[18px] mb-1.5 mt-5 text-[10px] font-bold uppercase tracking-[0.16em] text-[#8A99A3]">
        {activeCategory?.label ?? 'Integrations'}
      </div>

      {activeCategory?.integrations.slice(0, 6).map((integration) => (
        <TransitionLink
          key={integration.uid}
          href={`/${lang}/integrations/${integration.uid}`}
          onClick={onNavigate}
          className="grid grid-cols-[44px_1fr] items-center gap-3 px-5 py-3 text-inherit no-underline transition-colors hover:bg-[#FAFBFB]"
        >
          <span className="grid h-11 w-11 flex-shrink-0 place-items-center overflow-hidden rounded-xl border border-[#E4E7EA] bg-white">
            {integration.integration_mark?.url && (
              <Image
                src={integration.integration_mark.url}
                alt=""
                width={32}
                height={32}
                className="h-6 w-6 object-contain"
              />
            )}
          </span>
          <div className="min-w-0">
            <p className="m-0 mb-0.5 text-[14.5px] font-bold tracking-[-0.005em] text-[#1B2A33]">
              {integration.title.replace(/\s*NetSuite (Integration|Connector).*$/i, '')}
            </p>
            {integration.integration_teaser && (
              <p className="m-0 truncate text-[11.5px] leading-[1.35] text-[#5B6B75]">
                {integration.integration_teaser}
              </p>
            )}
          </div>
        </TransitionLink>
      ))}

      <TransitionLink
        href={`/${lang}/integrations`}
        onClick={onNavigate}
        className="flex items-center justify-between px-5 pb-1.5 pt-3.5 text-[13px] font-bold text-[#157A73] no-underline"
      >
        {viewAllText}
        <ArrowRight className="h-3.5 w-3.5" />
      </TransitionLink>
    </>
  );
}
